/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import BalloonEditor from "@ckeditor/ckeditor5-editor-balloon/src/ballooneditor.js";
import Autoformat from "@ckeditor/ckeditor5-autoformat/src/autoformat.js";
import AutoLink from "@ckeditor/ckeditor5-link/src/autolink.js";
import Alignment from "@ckeditor/ckeditor5-alignment/src/alignment.js";
import BlockQuote from "@ckeditor/ckeditor5-block-quote/src/blockquote.js";
import Bold from "@ckeditor/ckeditor5-basic-styles/src/bold.js";
import Essentials from "@ckeditor/ckeditor5-essentials/src/essentials.js";
import FontColor from "@ckeditor/ckeditor5-font/src/fontcolor.js";
import Heading from "@ckeditor/ckeditor5-heading/src/heading.js";
import HorizontalLine from "@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js";
import HtmlEmbed from "@ckeditor/ckeditor5-html-embed/src/htmlembed.js";
import Image from "@ckeditor/ckeditor5-image/src/image.js";
import ImageCaption from "@ckeditor/ckeditor5-image/src/imagecaption.js";
import ImageInsert from "@ckeditor/ckeditor5-image/src/imageinsert.js";
import ImageResize from "@ckeditor/ckeditor5-image/src/imageresize.js";
import ImageStyle from "@ckeditor/ckeditor5-image/src/imagestyle.js";
import ImageToolbar from "@ckeditor/ckeditor5-image/src/imagetoolbar.js";
import Indent from "@ckeditor/ckeditor5-indent/src/indent.js";
import Italic from "@ckeditor/ckeditor5-basic-styles/src/italic.js";
import Link from "@ckeditor/ckeditor5-link/src/link.js";
import List from "@ckeditor/ckeditor5-list/src/list.js";
import MediaEmbed from "@ckeditor/ckeditor5-media-embed/src/mediaembed.js";
import Mention from "@ckeditor/ckeditor5-mention/src/mention.js";
import Paragraph from "@ckeditor/ckeditor5-paragraph/src/paragraph.js";
import PasteFromOffice from "@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js";
import RemoveFormat from "@ckeditor/ckeditor5-remove-format/src/removeformat.js";
import Strikethrough from "@ckeditor/ckeditor5-basic-styles/src/strikethrough.js";
import Table from "@ckeditor/ckeditor5-table/src/table.js";
import TableToolbar from "@ckeditor/ckeditor5-table/src/tabletoolbar.js";
import TableCellProperties from "@ckeditor/ckeditor5-table/src/tablecellproperties";
import TableProperties from "@ckeditor/ckeditor5-table/src/tableproperties";
import TextTransformation from "@ckeditor/ckeditor5-typing/src/texttransformation.js";
import TodoList from "@ckeditor/ckeditor5-list/src/todolist";
import Underline from "@ckeditor/ckeditor5-basic-styles/src/underline.js";
import FileRepository from "@ckeditor/ckeditor5-upload/src/filerepository.js";
class Editor extends BalloonEditor {}

// Plugins to include in the build.
Editor.builtinPlugins = [
  Alignment,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Essentials,
  FileRepository,
  FontColor,
  Heading,
  HorizontalLine,
  HtmlEmbed,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  Indent,
  Italic,
  Link,
  List,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  Strikethrough,
  Table,
  TableToolbar,
  TableCellProperties,
  TableProperties,
  TextTransformation,
  TodoList,
  Underline,
];

// Editor configuration.
Editor.defaultConfig = {
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "fontColor",
      "|",
      "alignment",
      "bulletedList",
      "numberedList",
      "todoList",
      "indent",
      "outdent",
      "|",
      "link",
      "insertImage",
      "insertTable",
      "mediaEmbed",
      "blockQuote",
      "horizontalLine",
      "|",
      "htmlEmbed",
      "removeFormat",
    ],
  },
  language: "fr",
  heading: {
    options: [
      { model: "paragraph", title: "Paragraphe", class: "ck-heading_paragraph" },
      { model: "heading2", view: "h2", title: "Titre 2", class: "ck-heading_heading2" },
      { model: "heading3", view: "h3", title: "Titre 3", class: "ck-heading_heading3" },
      { model: "heading4", view: "h4", title: "Titre 4", class: "ck-heading_heading4" },
    ],
  },
  image: {
    toolbar: [
      "imageStyle:block",
      "imageStyle:side",
      "|",
      "imageResize:25",
      "imageResize:50",
      "imageResize:75",
      "imageResize:original",
      "|",
      "toggleImageCaption",
      "imageTextAlternative",
    ],
    insert: {
      type: "block",
    },
    resizeUnit: "%",
    resizeOptions: [
      { name: "imageResize:25", value: "25", label: "25%" },
      { name: "imageResize:50", value: "50", label: "50%" },
      { name: "imageResize:75", value: "75", label: "75%" },
      { name: "imageResize:original", value: null, label: "Originale" },
    ],
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "|",
      "tableCellProperties",
      "tableProperties",
    ],
  },
  link: {
    defaultProtocol: 'https://',
    decorators: {
      openInNewTab: {
        mode: 'automatic',
        callback: url => true,
        attributes: {
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }
    }
  },
  mediaEmbed: {
    previewsInData: true,
  },
  // Style des liens en bleu
  contentStyles: `
    .ck-content a {
      color: #007bff !important;
      text-decoration: underline;
    }
    .ck-content a:hover {
      color: #0056b3 !important;
    }
  `
};

export default Editor;
